import React, {createContext, useCallback, useContext, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {DEFAULT_LANGUAGE, isRTL, SupportedLanguage} from "./constants";
import {FluentProvider} from "i18n/FluentProvider";
import {announceLanguage, setGlobalLanguage} from "i18n/translator";
import {filterOnlySupported, isWidgetRoute} from "utils";

interface LanguageContextValue {
    lang: SupportedLanguage;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const useLanguage = (): LanguageContextValue => {
    const context = useContext(LanguageContext);

    if (!context) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }

    return context;
};

// The language we already know for this visitor: the URL wins, then the one the
// page is currently rendered in, then the last one we rendered for this browser
// (persisted by LanguageProvider). Null means we have never shown them the app.
const knownLanguage = (paramsLang?: string): SupportedLanguage | null =>
    filterOnlySupported(paramsLang)
    // @ts-ignore
    || filterOnlySupported(window.currentLanguage)
    || filterOnlySupported(localStorage.getItem("lang"));

// Query params that mean "this page load is a RETURN into an existing journey"
// (Stripe checkout, the guest claim link) rather than a first visit.
const RETURN_PARAMS = ["stripe", "session_id", "claim"];

const isJourneyReturn = (search: string): boolean => {
    const params = new URLSearchParams(search);
    return RETURN_PARAMS.some((param) => params.has(param));
};

// Select language to use. Never guesses: a visitor we know anything about keeps
// the language they are already in, everyone else gets English.
export const getLanguage = (paramsLang?: string): SupportedLanguage =>
    knownLanguage(paramsLang) || DEFAULT_LANGUAGE;

// Language for a URL that carries no /<lang> prefix. This is the ONLY place the
// device locale is allowed to matter, and only on a true first touch — a
// browser we have never rendered the app in, arriving somewhere that isn't a
// return into an existing journey. The choice is fixed on that first render
// (LanguageProvider persists it), so no later page load can flip the language
// mid-way: that is how an English-speaking user with an Arabic device ended up
// in the RTL app after paying (ID-2215).
export const getInitialLanguage = (search: string): SupportedLanguage => {
    const known = knownLanguage();
    if (known) {
        return known;
    }
    if (isJourneyReturn(search)) {
        return DEFAULT_LANGUAGE;
    }
    return filterOnlySupported(navigator.language.split("-")[0]) || DEFAULT_LANGUAGE;
}

// Best-effort: remembering the language must never break rendering it, and
// storage can be unavailable (blocked cookies/storage, enterprise policy).
export const setLanguage = (lang: SupportedLanguage) => {
    try {
        localStorage.setItem("lang", lang);
    } catch {
        // ignore — the language still applies to this page load
    }
}

export const pathWithLanguage = (path: string, search: string, lang: SupportedLanguage): string => {
    const pathSegments = path.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
        pathSegments[0] = lang;
        return `/${pathSegments.join('/')}${search}`;
    } else {
        return `/${lang}${search}`
    }
}

/** Switches the UI language in place: the URL prefix is the language, so this is a
 *  navigation, not a page load. */
export const useChangeLanguage = () => {
    const {lang} = useLanguage();
    const navigate = useNavigate();

    return useCallback((newLang: SupportedLanguage) => {
        if (newLang === lang) return;
        // window.location, not the router's: one-shot params (text, files_token, ucc…)
        // are stripped with history.replaceState behind the router's back, and its
        // stale copy would put them back into the URL.
        const {pathname, search, hash} = window.location;
        navigate(pathWithLanguage(pathname, search, newLang) + hash);
    }, [lang, navigate]);
};

// Everything that reads the language outside React context: getLanguage(),
// translate(), date formatting (<html lang>) and the [dir] styles. Published while
// the provider renders, not in an effect: after a switch the pages remount in this
// same pass, and their effects run before a parent's — they would read the old one.
const publishLanguage = (lang: SupportedLanguage) => {
    // @ts-ignore
    window.currentLanguage = lang
    setGlobalLanguage(lang)

    const direction = isRTL(lang) ? "rtl" : "ltr";
    const root = document.documentElement;
    if (root.getAttribute("dir") !== direction) {
        root.setAttribute("dir", direction);
    }
    if (root.getAttribute("lang") !== lang) {
        root.setAttribute("lang", lang);
    }
}

export const LanguageProvider = ({children, lang}: {lang: SupportedLanguage, children: React.ReactNode }) => {
    if (!lang) {
        throw new Error("LanguageProvider must be used with a language");
    }

    publishLanguage(lang);

    useEffect(() => {
        announceLanguage(lang);

        // Remember what we actually rendered, so a later full page load with no
        // language in the URL (an email link, a bare link) restores this one
        // instead of guessing. Embedded widgets render in the HOST page's
        // language, which is not the user's choice — they must not overwrite it.
        if (!isWidgetRoute()) {
            setLanguage(lang)
        }
    }, [lang]);


    return (
        <LanguageContext.Provider value={{lang: lang}}>
            <FluentProvider lang={lang}>
                {children}
            </FluentProvider>
        </LanguageContext.Provider>
    );
}

/** Remounts its subtree when the language changes, so copy a component keeps in
 *  state, memos or a third-party widget (Stripe Elements takes its locale once) is
 *  produced again in the new language. Wraps the pages, not the app shell: the
 *  shell's mount effects are the app's one-time init. */
export const LanguageBoundary = ({children}: {children: React.ReactNode}) => {
    const {lang} = useLanguage();
    return <React.Fragment key={lang}>{children}</React.Fragment>;
}
